import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

import { mapValueToMidiPitch, PitchSettings, ScaleName } from '../domain/pitch';
import { mapHeightRangeToVelocity, VelocitySettings } from '../domain/velocity';
import { resolveCategoryRoute, createDefaultCategoryRouting, CategoryRouting } from '../domain/categoryRouting';
import { normalizeNoteLengthMs, NoteLengthSettings } from '../domain/noteLength';
import { applyStartJitter, RngFn } from '../domain/jitter';
import { createLogger } from './logger';

// Re-export types for consumers
export type { VelocitySettings, NoteLengthSettings };

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Raw category data from a data point.
 */
export interface CategoryPointData {
  centroid?: number;
  count?: number;
  max_height?: number;
  min_height?: number;
}

/**
 * A single data point from the API.
 */
export interface DataPoint {
  time?: string;
  data: Record<string, CategoryPointData>;
  latitude?: number;
  longitude?: number;
}

/**
 * MIDI event output from dataPointToMidiEvents.
 */
export interface MIDIEvent {
  port: number;
  channel: number;
  note: number;
  velocity: number;
  duration: number;
  durationMs: number;
  when: Date;
  originalData: CategoryPointData;
}

/**
 * Result from velocityNoteDurationLookup.
 */
interface VelocityNoteDuration {
  velocity: number;
  note: number;
  duration: number;
  durationMs: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default settings
// ─────────────────────────────────────────────────────────────────────────────

const log = createLogger('player-utils');

const DEFAULT_PITCH_SETTINGS: PitchSettings = {
  minValue: 24,
  maxValue: 241,
  lowNote: 36,
  highNote: 84,
  scale: 'dorian' as ScaleName,
  root: 0,
};

const DEFAULT_VELOCITY_SETTINGS: VelocitySettings = {
  minDelta: 0,
  maxDelta: 241,
  minVelocity: 30,
  maxVelocity: 127,
};

const DEFAULT_NOTE_LENGTH_SETTINGS: NoteLengthSettings = {
  defaultMs: 100,
  minMs: 10,
  maxMs: 5000,
};

// ─────────────────────────────────────────────────────────────────────────────
// Core functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lookup velocity, note, and duration for a category data point.
 */
const velocityNoteDurationLookup = (
  categoryPoint: CategoryPointData | undefined,
  pitchSettings: PitchSettings | null | undefined,
  velocitySettings: VelocitySettings | null | undefined,
  noteLengthMs: number | null | undefined
): VelocityNoteDuration => {
  // Pitch source: use max_height (requested), fall back to centroid, then a neutral default.
  const pitchValue =
    typeof categoryPoint?.max_height === 'number'
      ? categoryPoint.max_height
      : (typeof categoryPoint?.centroid === 'number' ? categoryPoint.centroid : 60);
  const settings = pitchSettings ?? DEFAULT_PITCH_SETTINGS;
  const vSettings = velocitySettings ?? DEFAULT_VELOCITY_SETTINGS;

  const minH = categoryPoint?.min_height;
  const maxH = categoryPoint?.max_height;
  // Pass null as NaN - the velocity function handles non-finite values gracefully
  const heightRangeDelta = (typeof minH === 'number' && typeof maxH === 'number') ? (maxH - minH) : NaN;
  
  return {
    velocity: mapHeightRangeToVelocity(heightRangeDelta, vSettings),
    note: mapValueToMidiPitch(pitchValue, settings),
    duration: 0.5,
    durationMs: normalizeNoteLengthMs(noteLengthMs ?? undefined, DEFAULT_NOTE_LENGTH_SETTINGS),
  };
};

/**
 * Convert a data point to MIDI events based on category routing.
 *
 * @param dataPoint - The data point to convert
 * @param currentTime - Current playback time
 * @param categoryRouting - Optional routing config (defaults used if null)
 * @param offset - Seconds to add to event time (default 0)
 * @param pitchSettings - Optional pitch mapping settings
 * @param velocitySettings - Optional velocity mapping settings
 * @param noteLengthMs - Note length in milliseconds
 * @param noteStartJitterMs - Random jitter to add to note start (ms)
 * @param rng - Optional random number generator (defaults to Math.random)
 * @returns Array of MIDI events
 */
const dataPointToMidiEvents = (
  dataPoint: unknown,
  currentTime: string | Date | Dayjs,
  categoryRouting?: CategoryRouting | null,
  offset?: number | null,
  pitchSettings?: PitchSettings | null,
  velocitySettings?: VelocitySettings | null,
  noteLengthMs?: number | null,
  noteStartJitterMs?: number | null,
  rng?: RngFn
): MIDIEvent[] => {
  const effectiveOffset = offset ?? 0;

  if (!currentTime || !dataPoint) {
    throw new Error('currentTime and dataPoint are required');
  }

  // Validate dataPoint is a plain object
  if (typeof dataPoint !== 'object' || Array.isArray(dataPoint)) {
    throw new Error('dataPoint must be an object');
  }

  // TypeScript now knows dataPoint is object (but not null due to check above)
  const dp = dataPoint as DataPoint;

  // Use default routing if none provided
  const routing = categoryRouting ?? createDefaultCategoryRouting();

  if (!dp.time) {
    log.warn('dataPoint has no time', dp);
    return [];
  }
  if (!dp.data || Object.keys(dp.data).length === 0) {
    log.warn('dataPoint has no data', dp);
    return [];
  }
  
  const eventTime = dayjs.utc(dp.time).add(effectiveOffset, 'seconds');
  if (eventTime.isBefore(dayjs.utc(currentTime))) {
    return [];
  }

  const midiEvents = Object.keys(dp.data).map(key => {
    const { route, reason } = resolveCategoryRoute(key, routing);
    if (!route) {
      log.warn('Skipping category %s: %s', key, reason);
      return null;
    }
    const baseWhen = eventTime.toDate();
    // jitter function handles non-finite values (returns 0 delay)
    const when = applyStartJitter(baseWhen, noteStartJitterMs ?? 0, rng ?? Math.random);
    return {
      ...velocityNoteDurationLookup(dp.data[key], pitchSettings, velocitySettings, noteLengthMs),
      ...route,
      when,
      originalData: dp.data[key]
    };
  });

  return midiEvents.filter((e): e is MIDIEvent => e !== null);
};

export { dataPointToMidiEvents };
